/**
 * StripeConnectCard.jsx — Phase 3a Stripe Connect Express
 * onboarding card for the Provider Dashboard.
 *
 * Three visual states:
 *   - "not_connected": Set up payouts CTA → calls
 *     POST /api/providers/me/connect/onboarding-link, redirects
 *     to the returned Stripe-hosted URL.
 *   - "incomplete": amber "Action needed" badge + "Continue setup"
 *     button. Stripe's AccountLink is idempotent across re-requests
 *     for incomplete onboarding, so we call the same endpoint.
 *   - "active": green "Payouts active" badge.
 *
 * NEVER reads any Stripe secret. The publishable key is also not
 * needed in this phase — the entire onboarding flow is server-side
 * (Account + AccountLink), redirect-based.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { CreditCard, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

// Backlog D — when an admin is impersonating a provider, Stripe actions
// MUST be blocked. Stripe payouts/onboarding tie to the legitimate
// account-holder's identity, not the admin's, so we hard-disable rather
// than gate (a confirmation modal would still let the admin commit
// fraud-prone state changes to a third party's payout account).
const STRIPE_IMPERSONATION_TIP =
  "Stripe actions unavailable while impersonating — exit to return as admin.";

export function StripeConnectCard() {
  const { user } = useAuth();
  const isImpersonating = !!user?.is_impersonating;
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // { connected, charges_enabled, … }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/providers/me/connect/status");
        if (!cancelled) setStatus(data);
      } catch (e) {
        if (!cancelled)
          toast.error(
            e.response?.data?.detail || "Could not load Stripe Connect status."
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function startOnboarding() {
    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/providers/me/connect/onboarding-link"
      );
      // Hop over to Stripe's hosted onboarding. The link is short-lived;
      // refreshing the dashboard mints a fresh one because AccountLink
      // is single-use server-side.
      window.location.href = data.onboarding_url;
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          "Could not start Stripe onboarding. Try again in a moment."
      );
      setSubmitting(false);
    }
  }

  // -------- render --------
  let phase = "loading";
  if (!loading && status) {
    if (!status.connected) phase = "not_connected";
    else if (
      status.charges_enabled &&
      status.payouts_enabled &&
      status.details_submitted
    )
      phase = "active";
    else phase = "incomplete";
  }

  return (
    <section
      data-testid="stripe-connect-card"
      className="rounded-2xl border border-pp-border bg-pp-surface p-6 shadow-sm"
    >
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-brand-orange/10 p-2.5 text-brand-orange">
            <CreditCard className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-semibold text-pp-text">
              Payouts
            </h2>
            <p className="text-xs text-pp-text-muted">
              Powered by Stripe Connect
            </p>
          </div>
        </div>

        {phase === "active" && (
          <Badge
            data-testid="stripe-status-badge-active"
            className="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50"
          >
            <CheckCircle2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            Payouts active
          </Badge>
        )}
        {phase === "incomplete" && (
          <Badge
            data-testid="stripe-status-badge-incomplete"
            className="border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-50"
          >
            <AlertTriangle className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            Action needed
          </Badge>
        )}
      </header>

      <div className="mt-5">
        {phase === "loading" && (
          <p className="text-sm text-pp-text-muted">Checking status…</p>
        )}

        {phase === "not_connected" && (
          <>
            <p className="mb-4 text-sm text-pp-text-muted">
              Connect a Stripe account to receive payouts for completed jobs.
              We'll send you to Stripe's secure hosted onboarding and bring
              you back here when it's done.
            </p>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <Button
                      data-testid="stripe-connect-setup-button"
                      onClick={startOnboarding}
                      disabled={submitting || isImpersonating}
                      aria-disabled={isImpersonating || undefined}
                    >
                      {submitting ? "Redirecting…" : "Set up payouts"}
                    </Button>
                  </span>
                </TooltipTrigger>
                {isImpersonating && (
                  <TooltipContent
                    side="top"
                    align="start"
                    data-testid="stripe-connect-impersonation-tip"
                  >
                    <p className="text-xs">{STRIPE_IMPERSONATION_TIP}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </>
        )}

        {phase === "incomplete" && (
          <>
            <p className="mb-4 text-sm text-pp-text-muted">
              Stripe still needs a few details before we can release payouts.
              Pick up where you left off.
            </p>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <Button
                      data-testid="stripe-connect-continue-button"
                      onClick={startOnboarding}
                      disabled={submitting || isImpersonating}
                      aria-disabled={isImpersonating || undefined}
                    >
                      {submitting ? "Redirecting…" : "Continue setup"}
                    </Button>
                  </span>
                </TooltipTrigger>
                {isImpersonating && (
                  <TooltipContent
                    side="top"
                    align="start"
                    data-testid="stripe-connect-impersonation-tip-incomplete"
                  >
                    <p className="text-xs">{STRIPE_IMPERSONATION_TIP}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </>
        )}

        {phase === "active" && (
          <p className="text-sm text-pp-text-muted">
            Your Stripe account is fully verified and payouts are enabled.
          </p>
        )}
      </div>
    </section>
  );
}

export default StripeConnectCard;
